import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Loader2, MessageCircle, Send, RefreshCw, CheckCircle, XCircle, Square, ChevronLeft, Bot, Type } from 'lucide-react';
import { API_URL } from '../../config';

// ============================================================
// Répondre aux commentaires DÉJÀ reçus sur une publication
// (les réponses automatiques classiques ne couvrent que les nouveaux commentaires).
// Envoi échelonné côté serveur (~8 s entre deux réponses) pour éviter l'effet spam.
// ============================================================

const headers = () => ({
  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
  'Content-Type': 'application/json',
});

const STATUS = {
  a_repondre: { label: 'À répondre', cls: 'text-yellow-300 bg-yellow-500/10' },
  a_deja_des_reponses: { label: 'Déjà des réponses', cls: 'text-gray-300 bg-white/10' },
  repondu_teckia: { label: 'Répondu par TeckiA', cls: 'text-green-300 bg-green-500/10' },
};

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return ''; }
};

export default function ExistingCommentsReplier({ clientId, canEdit, defaultFixedText = '', postConfigs = [] }) {
  const [posts, setPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);       // publication choisie
  const [comments, setComments] = useState([]);
  const [toReply, setToReply] = useState(0);
  const [loadingComments, setLoadingComments] = useState(false);
  const [mode, setMode] = useState('ai');                // 'ai' | 'fixed'
  const [fixedText, setFixedText] = useState(defaultFixedText);
  const [postInfo, setPostInfo] = useState('');
  const [skipWithReplies, setSkipWithReplies] = useState(true);
  const [starting, setStarting] = useState(false);
  const [job, setJob] = useState(null);                  // { job_id, ...état }
  const pollRef = useRef(null);

  const base = `${API_URL}/api/bot/config/${clientId}`;

  const loadPosts = useCallback(async () => {
    setLoadingPosts(true); setError('');
    try {
      const res = await fetch(`${base}/page-posts`, { headers: headers() });
      const d = await res.json();
      if (d.success) setPosts(d.posts); else setError(d.message || 'Impossible de charger les publications.');
    } catch { setError('Erreur réseau.'); }
    setLoadingPosts(false);
  }, [base]);

  const openPost = useCallback(async (post) => {
    setSelected(post); setComments([]); setToReply(0); setLoadingComments(true); setError(''); setJob(null);
    // Reprend la reponse reglee sur cette publication (Reponse aux commentaires > Publications selectionnees)
    const cfg = postConfigs.find(c => c.post_id === post.id);
    if (cfg && (cfg.reply_mode === 'ai' || cfg.reply_mode === 'fixed')) setMode(cfg.reply_mode);
    setFixedText(cfg?.reply_text || defaultFixedText);
    setPostInfo(cfg?.reply_info || '');
    try {
      const res = await fetch(`${base}/post-comments?post_id=${encodeURIComponent(post.id)}`, { headers: headers() });
      const d = await res.json();
      if (d.success) { setComments(d.comments); setToReply(d.to_reply); }
      else setError(d.message || 'Impossible de charger les commentaires.');
    } catch { setError('Erreur réseau.'); }
    setLoadingComments(false);
  }, [base, postConfigs, defaultFixedText]);

  const stopPolling = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  useEffect(() => stopPolling, []);

  const poll = useCallback((jobId) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch(`${base}/backfill-replies/${jobId}`, { headers: headers() });
        const d = await res.json();
        if (d.success) {
          setJob({ job_id: jobId, ...d });
          if (d.status !== 'running') { stopPolling(); if (selected) openPostQuiet(selected); }
        } else { stopPolling(); }
      } catch { /* on réessaie au prochain tick */ }
    }, 4000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base, selected]);

  // Rafraîchit la liste des commentaires sans effacer l'écran de progression
  const openPostQuiet = async (post) => {
    try {
      const res = await fetch(`${base}/post-comments?post_id=${encodeURIComponent(post.id)}`, { headers: headers() });
      const d = await res.json();
      if (d.success) { setComments(d.comments); setToReply(d.to_reply); }
    } catch { /* ignore */ }
  };

  const start = async () => {
    setStarting(true); setError('');
    try {
      const res = await fetch(`${base}/backfill-replies`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ post_id: selected.id, mode, fixed_text: fixedText, post_info: postInfo, skip_with_replies: skipWithReplies }),
      });
      const d = await res.json();
      if (d.success) {
        setJob({ job_id: d.job_id, status: 'running', total: d.total, done: 0, replied: 0, failed: 0 });
        poll(d.job_id);
      } else setError(d.message || "Impossible de lancer l'envoi.");
    } catch { setError('Erreur réseau.'); }
    setStarting(false);
  };

  const cancel = async () => {
    if (!job) return;
    await fetch(`${base}/backfill-replies/${job.job_id}/cancel`, { method: 'POST', headers: headers() });
  };

  const running = job?.status === 'running';
  const eligible = skipWithReplies ? toReply : comments.filter(c => c.status !== 'repondu_teckia').length;
  const canStart = canEdit && !running && !starting && eligible > 0 && (mode === 'ai' || fixedText.trim());

  return (
    <div>
      {!selected ? (
        <>
          <div className="flex items-center justify-between mb-3 gap-3">
            <p className="text-gray-400 text-xs">
              Choisissez une publication : TeckiA répond aux commentaires déjà reçus (les réponses automatiques classiques ne traitent que les nouveaux).
            </p>
            <button onClick={loadPosts} disabled={loadingPosts}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium disabled:opacity-50 flex-shrink-0">
              {loadingPosts ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              {posts.length ? 'Actualiser' : 'Charger mes publications'}
            </button>
          </div>
          {error && <p className="text-red-300 text-xs mb-2">{error}</p>}
          <div className="space-y-1.5 max-h-80 overflow-y-auto">
            {posts.map(p => (
              <button key={p.id} onClick={() => openPost(p)}
                className="w-full text-left flex items-center gap-3 bg-black/20 hover:bg-white/10 rounded-lg px-3 py-2.5 transition">
                <MessageCircle className="w-4 h-4 text-blue-300 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-white text-xs truncate">{p.message || '(publication sans texte)'}</div>
                  <div className="text-gray-500 text-[10px]">{fmtDate(p.created_time)}</div>
                </div>
                <span className="text-gray-300 text-xs flex-shrink-0">{p.comments_count} commentaire{p.comments_count > 1 ? 's' : ''}</span>
              </button>
            ))}
          </div>
        </>
      ) : (
        <>
          <button onClick={() => { stopPolling(); setSelected(null); setJob(null); }}
            className="flex items-center gap-1 text-blue-300 hover:text-blue-200 text-xs mb-3">
            <ChevronLeft className="w-3.5 h-3.5" /> Retour aux publications
          </button>
          <div className="bg-black/20 rounded-lg px-3 py-2 mb-3">
            <div className="text-white text-xs truncate">{selected.message || '(publication sans texte)'}</div>
            <div className="text-gray-500 text-[10px]">{fmtDate(selected.created_time)} · {comments.length} commentaires lus · {toReply} à répondre</div>
          </div>

          {loadingComments ? (
            <div className="py-4 text-center"><Loader2 className="w-5 h-5 text-blue-400 animate-spin mx-auto" /></div>
          ) : (
            <>
              <div className="space-y-1 max-h-48 overflow-y-auto mb-4">
                {comments.map(c => (
                  <div key={c.id} className="flex items-start gap-2 bg-white/5 rounded-lg px-3 py-1.5">
                    <div className="flex-1 min-w-0 text-gray-200 text-xs break-words">{c.message || '(sans texte)'}</div>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded flex-shrink-0 ${STATUS[c.status]?.cls}`}>{STATUS[c.status]?.label}</span>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                <button onClick={() => setMode('ai')}
                  className={`text-left p-3 rounded-xl border transition ${mode === 'ai' ? 'bg-blue-500/20 border-blue-400/50' : 'bg-white/5 border-white/10'}`}>
                  <div className="text-white text-sm flex items-center gap-1.5"><Bot className="w-4 h-4" /> Réponse par l'IA</div>
                  <div className="text-gray-400 text-xs mt-0.5">Une réponse adaptée à chaque commentaire, dans sa langue</div>
                </button>
                <button onClick={() => setMode('fixed')}
                  className={`text-left p-3 rounded-xl border transition ${mode === 'fixed' ? 'bg-blue-500/20 border-blue-400/50' : 'bg-white/5 border-white/10'}`}>
                  <div className="text-white text-sm flex items-center gap-1.5"><Type className="w-4 h-4" /> Message automatique</div>
                  <div className="text-gray-400 text-xs mt-0.5">Le même texte pour tous les commentaires</div>
                </button>
              </div>
              {mode === 'fixed' && (
                <input value={fixedText} onChange={e => setFixedText(e.target.value)} maxLength={255}
                  placeholder="Ex : Merci pour votre commentaire ! Écrivez-nous en message privé 🙂"
                  className="w-full mb-3 bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-500" />
              )}
              {mode === 'ai' && (
                <textarea value={postInfo} onChange={e => setPostInfo(e.target.value)} maxLength={2000} rows={3}
                  placeholder="Infos sur cette publication pour l'IA (date de sortie, prix, lien, consignes…) — optionnel"
                  className="w-full mb-3 bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white text-xs placeholder-gray-500" />
              )}
              <label className="flex items-center gap-2 text-gray-300 text-xs mb-3 cursor-pointer">
                <input type="checkbox" checked={skipWithReplies} onChange={e => setSkipWithReplies(e.target.checked)} />
                Ignorer les commentaires qui ont déjà une réponse (recommandé)
              </label>

              {error && <p className="text-red-300 text-xs mb-2">{error}</p>}
              {!canEdit && <p className="text-yellow-300 text-xs mb-2">Configuration gérée par l'équipe TDI DIGITAL : envoi indisponible depuis votre compte.</p>}

              {job && (
                <div className="bg-black/30 border border-white/10 rounded-xl p-3 mb-3">
                  <div className="flex items-center justify-between text-xs mb-2">
                    <span className="text-white flex items-center gap-1.5">
                      {running && <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-300" />}
                      {job.status === 'done' && <CheckCircle className="w-3.5 h-3.5 text-green-300" />}
                      {(job.status === 'error' || job.status === 'cancelled') && <XCircle className="w-3.5 h-3.5 text-red-300" />}
                      {running ? 'Envoi en cours…' : job.status === 'done' ? 'Terminé' : job.status === 'cancelled' ? 'Arrêté' : 'Interrompu'}
                    </span>
                    <span className="text-gray-300">{job.replied} envoyée{job.replied > 1 ? 's' : ''} / {job.total}{job.failed ? ` · ${job.failed} échec${job.failed > 1 ? 's' : ''}` : ''}</span>
                  </div>
                  <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500 transition-all" style={{ width: `${job.total ? (job.done / job.total) * 100 : 0}%` }} />
                  </div>
                  {job.error && <p className="text-red-300 text-xs mt-2">{job.error}</p>}
                  {running && (
                    <button onClick={cancel} className="mt-2 flex items-center gap-1.5 text-red-300 hover:text-red-200 text-xs">
                      <Square className="w-3 h-3" /> Arrêter l'envoi
                    </button>
                  )}
                </div>
              )}

              <button onClick={start} disabled={!canStart}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-green-600 hover:bg-green-500 text-white text-sm font-medium disabled:opacity-40">
                {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Répondre à {eligible} commentaire{eligible > 1 ? 's' : ''}
              </button>
              <p className="text-gray-500 text-[10px] mt-2">
                Envoi échelonné (environ 8 secondes entre deux réponses, 150 maximum par lancement) pour éviter d'être pris pour du spam. Vous pouvez quitter cette page : l'envoi continue.
              </p>
            </>
          )}
        </>
      )}
    </div>
  );
}
